import {
  FC,
  createContext,
  useCallback,
  useEffect,
  useReducer,
  useState,
} from "react";
import {
  ICreateUserProgramRequest,
  getUserProgram,
  createUserProgram,
} from "../../api/user-program";
import { AxiosError } from "axios";
import { FullPageSpinner } from "../../components/shared/full-page-spinner";
import { TUserProgramContext, TUserProgramState } from "./types";
import { reducer } from "./reducer";
import { ServerError } from "../../components/5XX";
import { useNavigate } from "react-router-dom";

export const UserProgramContext = createContext<TUserProgramContext>({
  networkError: null,
  unauthorized: false,
  role: "patient",
  clinic: "",
  _id: "",
  userPayload: {},
  setUnauthorized: () => {},
  program: null,
  dispatch: () => {},
  loading: false,
  fetchProgram: () => {},
});

const initialProgramState = {
  loading: true,
  program: null,
};

type Props = {
  children: React.ReactNode;
};

export const UserProgramContextProvider: FC<Props> = ({ children }) => {
  const [programState, dispatch] = useReducer(
    reducer,
    initialProgramState as TUserProgramState
  );
  const navigate = useNavigate();
  const [networkError, setNetworkError] = useState<Error | null>(null);
  const [unauthorized, setUnauthorized] = useState<boolean>(false);
  const [role, setRole] = useState<string>("");
  const [clinic, setClinic] = useState<string | null>(null);
  const [_id, setId] = useState<string>("");
  const [userPayload, setUserPayload] = useState<Object>({});
  const [loading, setLoading] = useState(false);

  const clinicUserMap = new Map([
    ["clinic-dashboard", ""],
    ["clinic-weight", "PWS-001"],
    ["admin", "all"],
  ]);

  const getUserClinic = (role: string) => {
    return clinicUserMap.get(role);
  };

  const handleError = useCallback((error: AxiosError) => {
    const { message } = error;

    const createProgram = async (id: string) => {
      const requestData: ICreateUserProgramRequest = {
        userId: id,
      };

      const result = await createUserProgram(requestData);
      console.log("Program Create:", result);

      fetchProgram();
    };

    if (!error.response) {
      setNetworkError(new Error(message));
      return;
    }

    const {
      response: { status, data },
    } = error;

    if (status === 404) {
      const { user } = data as { user: { _id: string; role: string } };
      if (
        user._id &&
        [
          "patient",
          "clinic-preview",
          "clinic-dashboard",
          "admin",
          "employee",
          "clinic-weight",
        ].includes(user.role)
      ) {
        setId(user._id);

        createProgram(user._id);

        if (user.role) {
          setRole(user.role);
          setClinic(getUserClinic(user.role) ?? null);
        }

        navigate("/");
      }
      return;
    }

    if (!status || [401, 403].includes(status)) {
      setUnauthorized(true);
      return;
    }

    setNetworkError(new Error(message));
  }, []);

  const fetchProgram = useCallback(async () => {
    setLoading(true);
    dispatch({
      type: "START_FETCH_PROGRAM",
    });

    setUnauthorized(false);

    return getUserProgram()
      .then((data) => {
        setRole(data?.user.role);
        setClinic(getUserClinic(data?.user.role) ?? null);
        setUserPayload(data?.user);
        if (data?.user) {
          data.user.clinic = getUserClinic(data?.user?.role);
        }
        dispatch({
          type: "FETCH_PROGRAM",
          payload: {
            program: data,
          },
        });
      })
      .catch(handleError)
      .finally(() => {
        dispatch({
          type: "FINISH_FETCH_PROGRAM",
        });
        setLoading(false);
      });
  }, [handleError]);

  useEffect(() => {
    fetchProgram();
  }, [fetchProgram]);

  if (loading) {
    return <FullPageSpinner />;
  }

  if (programState.loading && !networkError) {
    return <FullPageSpinner />;
  }

  if (networkError) {
    return <ServerError />;
  }

  const contextValue = {
    networkError,
    unauthorized,
    program: programState.program,
    dispatch,
    loading: programState.loading,
    fetchProgram,
    setUnauthorized,
    role,
    _id,
    userPayload,
  };

  return (
    <UserProgramContext.Provider value={contextValue}>
      {children}
    </UserProgramContext.Provider>
  );
};
